import { useCallback, useEffect, useMemo, useState } from "react";
import { realtimeApi, RealtimeCountryRow } from "../../services/realtimeApi";
import { usePollingRefresh } from "./usePollingRefresh";
import { roamingZonesFrom } from "../../utils/realtimeCountry";
import type { RoamingZone } from "../../components/realtime/RoamingPulse";

export type RoamingInterval = 10 | 20 | 30;
export const ROAMING_INTERVALS: RoamingInterval[] = [10, 20, 30];

export interface RoamingPulseState {
  zones: RoamingZone[];
  updatedAt: number | null;
  interval: RoamingInterval;
  setInterval: (m: RoamingInterval) => void;
  loading: boolean;
  error: string | null;
  notEnabled: boolean;
}

/**
 * Live roaming board data: international presence per zone and its change
 * over the chosen interval. Always "now" — it keeps its own short raw window
 * rather than following a date picker, because over a wide range the backend
 * aggregates to hour/day buckets and that would erase the very change being
 * reported. Refreshed every minute while `enabled`.
 */
export function useRoamingPulse(opts: {
  enabled: boolean;
  scope: Set<string> | null; // target ids to include; null = all
}): RoamingPulseState {
  const { enabled, scope } = opts;
  const [rows, setRows] = useState<RealtimeCountryRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notEnabled, setNotEnabled] = useState(false);
  const [interval, setInterval] = useState<RoamingInterval>(10);

  const load = useCallback(async () => {
    try {
      setError(null);
      setNotEnabled(false);
      const data = await realtimeApi.getRealtimeDataCountry(
        10000,
        undefined,
        new Date(Date.now() - 3 * 3600 * 1000).toISOString()
      );
      setRows(data.rows);
    } catch (err) {
      if ((err as { status?: number }).status === 403) setNotEnabled(true);
      else setError(err instanceof Error ? err.message : "Failed to load roaming data");
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    load().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, load]);

  usePollingRefresh(() => load(), 60000, enabled);

  const { zones, updatedAt } = useMemo(
    () => roamingZonesFrom(rows, scope, interval),
    [rows, scope, interval]
  );

  return {
    zones,
    updatedAt,
    interval,
    setInterval,
    loading: loading && !rows, // hold the previous board during refreshes
    error,
    notEnabled,
  };
}
